import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Crown, Radio, ShieldCheck, Loader2 } from "lucide-react";
import { api, wsUrl } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { LeaderboardBoard } from "@/components/LeaderboardBoard";
import { BidModal } from "@/components/BidModal";
import { BoardSeo } from "@/components/BoardSeo";
import { EmptyArena } from "@/components/EmptyArena";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

function setNoIndex(noindex) {
  let tag = document.querySelector('meta[name="robots"]');
  if (!tag) {
    tag = document.createElement("meta");
    tag.name = "robots";
    document.head.appendChild(tag);
  }
  tag.content = noindex ? "noindex, nofollow" : "index, follow";
}

// Merge incoming leaderboard with the previously loaded one so a fully-rendered
// entry (name/website/bid) is never replaced by a partial/empty update.
const DISPLAY_FIELDS = ["business_name", "website", "logo_url", "effective_bid", "max_daily_cap", "whatsapp_number", "location_text", "short_description", "google_maps_url", "phone_number"];
function mergeLeaderboard(prev, incoming) {
  if (!Array.isArray(incoming)) return prev;
  const prevById = {};
  (prev || []).forEach((e) => { prevById[e.user_id] = e; });
  return incoming.map((entry) => {
    const old = prevById[entry.user_id];
    if (!old) return entry;
    const merged = { ...old, ...entry };
    // if an incoming display field is missing or empty, keep the previous value
    DISPLAY_FIELDS.forEach((f) => {
      const val = entry[f];
      if (val === undefined || val === null || val === "") merged[f] = old[f];
    });
    return merged;
  });
}

export default function KeywordBoard() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [keyword, setKeyword] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(false);
  const [bidOpen, setBidOpen] = useState(false);
  const [nudgeCap, setNudgeCap] = useState(null);
  const wsRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/keywords/${slug}`);
      setKeyword(data.keyword);
      setEntries(data.leaderboard);
      setNoIndex(!data.keyword.is_indexed);
    } catch {}
    setLoading(false);
  }, [slug]);

  // Optimistic update: apply the leaderboard returned by the bid request
  // immediately, then refresh keyword meta in the background.
  const handleBidSuccess = useCallback((leaderboard) => {
    if (Array.isArray(leaderboard)) setEntries((prev) => mergeLeaderboard(prev, leaderboard));
    load();
  }, [load]);

  useEffect(() => {
    load();
    const ws = new WebSocket(wsUrl(slug));
    wsRef.current = ws;
    ws.onopen = () => setLive(true);
    ws.onclose = () => setLive(false);
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === "leaderboard" && msg.slug === slug) {
          setEntries((prev) => mergeLeaderboard(prev, msg.entries));
        }
      } catch {}
    };
    return () => ws.close();
  }, [slug, load]);

  useEffect(() => () => setNoIndex(false), []);

  // Deep link: /k/slug?bid=1 auto-opens the bid modal (used by "Reclaim #1").
  useEffect(() => {
    if (!loading && user && searchParams.get("bid") === "1") {
      setBidOpen(true);
      searchParams.delete("bid");
      setSearchParams(searchParams, { replace: true });
    }
  }, [loading, user, searchParams, setSearchParams]);

  const myEntry = user ? entries.find((e) => e.user_id === user.user_id) : null;

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="flex justify-center pt-40"><Loader2 className="w-8 h-8 animate-spin text-white/40" /></div>
      </div>
    );
  }

  return (
    <div>
      <BoardSeo keywordName={keyword?.name} entries={entries} boardPath={`/k/${slug}`} />
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 pt-28 pb-24">
        <button data-testid="back-btn" onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm text-white/40 hover:text-white transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-white/40">Keyword board</span>
              {keyword?.verified ? (
                <span className="inline-flex items-center gap-1 text-xs text-yellow-400"><ShieldCheck className="w-3.5 h-3.5" /> Verified</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs text-white/30">Unverified · noindex</span>
              )}
            </div>
            <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tighter capitalize">
              {keyword?.name}
            </h1>
            <div className="flex items-center gap-2 mt-3 text-sm text-white/50">
              <span className={`inline-flex items-center gap-1 ${live ? "text-emerald-400" : "text-white/30"}`}>
                <Radio className="w-3.5 h-3.5" /> {live ? "Live" : "Connecting…"}
              </span>
              <span>·</span>
              <span>{entries.length} {entries.length === 1 ? "bidder" : "bidders"}</span>
            </div>
          </div>

          <Button data-testid="place-bid-btn" onClick={() => setBidOpen(true)} className="rounded-full h-12 px-6 bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 active:scale-95 transition-all font-bold text-black shrink-0">
            <Crown className="w-4 h-4 mr-2" /> {myEntry ? `You're #${myEntry.rank} · Edit bid` : "Bid for #1"}
          </Button>
        </div>

        {entries.length === 0 ? (
          <EmptyArena keywordName={keyword?.name || slug} onClaim={() => setBidOpen(true)} />
        ) : (
          <LeaderboardBoard entries={entries} keywordName={keyword?.name} onClaim={() => setBidOpen(true)} onNudge={(cap) => { setNudgeCap(cap); setBidOpen(true); }} showPhone={true} />
        )}

        <div className="mt-10 glass rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-sm text-white/60">Is this your brand? Lock it permanently.</div>
          <Button data-testid="brand-lock-cta" variant="outline" onClick={() => navigate("/dashboard?tab=brandlock&keyword=" + encodeURIComponent(keyword?.name || slug))} className="rounded-full border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10 hover:text-yellow-300">
            <ShieldCheck className="w-4 h-4 mr-2" /> Apply for Brand Lock
          </Button>
        </div>
      </div>

      <BidModal
        open={bidOpen}
        onOpenChange={(o) => { setBidOpen(o); if (!o) setNudgeCap(null); }}
        slug={slug}
        keywordName={keyword?.name}
        topBid={entries[0]?.locked_bid_amount}
        existingBid={myEntry || null}
        prefillCap={nudgeCap}
        onSuccess={handleBidSuccess}
      />
    </div>
  );
}
